import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shopBrandStyles } from '../../styles/shop-brand-styles';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { ApiFactory } from '../../api/easypos-api';
import { checkForUpdate, applyUpdate } from '../../sw-register';
import type { UpdateCheckResult } from '../../sw-register';

@customElement('app-about')
export class AppAbout extends WithUserProfileConsumer(LitElement) {
  @state() private _serverVersion: string | null = null;
  @state() private _serverVersionError = false;
  @state() private _updateChecking = false;
  @state() private _updateResult: UpdateCheckResult | null = null;

  static styles = [
    shopBrandStyles,
    css`
      :host {
        display: block;
      }

      .about-page {
        max-width: 560px;
        margin: 0 auto;
        padding: clamp(1.5rem, 5vw, 3rem) clamp(1.25rem, 4vw, 2rem) 3rem;
        background: var(--s-canvas);
        color: var(--s-ink);
        font-family: var(--font-body);
        -webkit-font-smoothing: antialiased;
        box-sizing: border-box;
      }

      /* ===== Brand hero ===== */
      .brand-hero {
        text-align: center;
        padding: 2rem 0 2.25rem;
      }
      .brand-mark {
        display: inline-flex;
        align-items: center;
        gap: 0.85rem;
        margin-bottom: 1.25rem;
      }
      .brand-mark img {
        width: 64px;
        height: 64px;
        border-radius: 16px;
      }
      .brand-wordmark {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 36px;
        letter-spacing: -0.03em;
        color: var(--s-navy);
        line-height: 1;
      }
      .brand-wordmark span {
        color: var(--s-gold);
      }
      :host-context(.sl-theme-dark) .brand-wordmark {
        color: var(--s-ink);
      }

      .byline {
        font-family: var(--font-display);
        font-weight: 500;
        font-size: clamp(1.05rem, 3.5vw, 1.3rem);
        line-height: 1.4;
        letter-spacing: -0.015em;
        color: var(--s-ink-soft);
        max-width: 28ch;
        margin: 0 auto;
      }
      .byline em {
        font-style: normal;
        color: var(--s-gold);
        font-weight: 600;
      }

      /* ===== Info cards ===== */
      .card {
        background: var(--s-canvas);
        border: 1px solid var(--s-line);
        border-radius: 14px;
        padding: 1.1rem 1.25rem;
        box-shadow: var(--s-shadow-soft);
        margin-bottom: 0.75rem;
      }
      .card-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--s-ink-faint);
        margin-bottom: 0.7rem;
      }

      /* ===== Version row ===== */
      .version-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.25rem;
        font-variant-numeric: tabular-nums;
      }
      .version-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .version-item-name {
        font-size: 12px;
        font-weight: 600;
        color: var(--s-ink-faint);
      }
      .version-item-value {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.1rem;
        color: var(--s-ink);
        line-height: 1.1;
        overflow-wrap: anywhere;
      }
      .version-item-value.warn {
        color: var(--s-warn);
      }
      .skew-note {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px dashed var(--s-line);
        font-size: 0.82rem;
        color: var(--s-warn);
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      /* ===== Update check ===== */
      .update-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
        margin-top: 0.5rem;
      }
      .update-result {
        font-size: 0.85rem;
        color: var(--s-ink-soft);
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      .update-result.warn {
        color: var(--s-warn);
      }
      .update-result.bad {
        color: var(--s-bad);
      }
      .update-result.good {
        color: var(--s-good);
      }

      /* ===== Legal links ===== */
      .legal-row {
        display: flex;
        align-items: center;
        gap: 1.5rem;
      }
      .legal-link {
        color: var(--s-ink);
        text-decoration: none;
        font-weight: 600;
        font-size: 0.9rem;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
      }
      .legal-link:hover {
        color: var(--s-gold);
      }
      .legal-link sl-icon {
        font-size: 14px;
        color: var(--s-ink-faint);
      }

      /* ===== Copyright ===== */
      .copyright {
        margin-top: 2rem;
        text-align: center;
        font-size: 0.8rem;
        color: var(--s-ink-faint);
        line-height: 1.5;
      }
      .copyright .dot {
        color: var(--s-ink-faint);
        margin: 0 6px;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadServerVersion();
  }

  private async _loadServerVersion() {
    const result = await ApiFactory.anonymous().getServerVersion();
    if (result.success && result.data) {
      this._serverVersion = result.data;
    } else {
      this._serverVersionError = true;
    }
  }

  private async _onCheckForUpdate() {
    if (this._updateChecking) return;
    this._updateChecking = true;
    this._updateResult = null;
    try {
      this._updateResult = await checkForUpdate();
    } finally {
      this._updateChecking = false;
    }
  }

  private _onApplyUpdate() {
    if (this._updateResult?.kind !== 'update-available') return;
    applyUpdate(this._updateResult.registration);
  }

  private _renderUpdateResult() {
    if (!this._updateResult) return nothing;
    const r = this._updateResult;
    if (r.kind === 'up-to-date') {
      return html`<span class="update-result good"
        ><sl-icon name="check-circle"></sl-icon> You're up to date.</span
      >`;
    }
    if (r.kind === 'update-available') {
      return html`
        <span class="update-result warn">
          <sl-icon name="arrow-down-circle"></sl-icon>
          A new version is ready.
        </span>
        <button class="shop-btn gold" @click=${this._onApplyUpdate}>
          Reload to apply
        </button>
      `;
    }
    if (r.kind === 'no-service-worker') {
      return html`<span class="update-result"
        >Service worker unavailable (dev build).</span
      >`;
    }
    return html`<span class="update-result bad"
      ><sl-icon name="exclamation-circle"></sl-icon> Couldn't check:
      ${r.message}</span
    >`;
  }

  render() {
    const serverLabel = this._serverVersionError
      ? 'unavailable'
      : (this._serverVersion ?? '…');
    const skew =
      this._serverVersion !== null && this._serverVersion !== __APP_VERSION__;
    const year = new Date().getFullYear();

    return html`
      <div class="about-page">
        <div class="brand-hero">
          <div class="brand-mark">
            <img src="/assets/icons/pag-asenso-icon.svg" alt="" />
            <span class="brand-wordmark">Pag<span>-asenso</span></span>
          </div>
          <p class="byline">
            The POS that grows with you, from <em>entrepreneur</em> to
            <em>enterprise</em>.
          </p>
        </div>

        <div class="card">
          <div class="card-label">Version</div>
          <div class="version-row">
            <div class="version-item">
              <span class="version-item-name">App</span>
              <span class="version-item-value ${skew ? 'warn' : ''}"
                >${__APP_VERSION__}</span
              >
            </div>
            <div class="version-item">
              <span class="version-item-name">Server</span>
              <span class="version-item-value ${skew ? 'warn' : ''}"
                >${serverLabel}</span
              >
            </div>
          </div>
          ${
            skew
              ? html`
                  <div class="skew-note">
                    <sl-icon name="exclamation-triangle"></sl-icon>
                    Version skew — reload to update the app.
                  </div>
                `
              : nothing
          }
          <div class="update-row" style="margin-top: 0.9rem;">
            <button
              class="shop-btn ghost"
              ?disabled=${this._updateChecking}
              @click=${this._onCheckForUpdate}
            >
              ${
                this._updateChecking
                  ? html`<sl-spinner style="font-size: 14px;"></sl-spinner>
                      Checking…`
                  : html`<sl-icon name="arrow-repeat"></sl-icon> Check for
                      updates`
              }
            </button>
            ${this._renderUpdateResult()}
          </div>
        </div>

        <div class="card">
          <div class="card-label">Legal</div>
          <div class="legal-row">
            <a class="legal-link" href="/privacy">
              <sl-icon name="shield-check"></sl-icon>
              Privacy Policy
            </a>
            <a class="legal-link" href="/terms">
              <sl-icon name="file-text"></sl-icon>
              Terms of Use
            </a>
          </div>
        </div>

        <p class="copyright">
          © ${year} Pag-asenso<span class="dot">·</span>All rights reserved.
        </p>
      </div>
    `;
  }
}
